import { Pause, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { formatDuration } from "@/shared/lib/labels";

/** Meditáció-lejátszó (design-rendszer 6.): `gold-soft` kártya, 52 px-es `ink` play-gomb
 *  arany ikonnal — ez az egyetlen sötétkék kitöltött elem a felületen. */
export function AudioPlayer({
  src,
  title,
  seconds,
  onStart,
  onComplete,
}: {
  src: string;
  title: string;
  seconds?: number | null;
  /** Az első elindításkor (lejátszásonként egyszer, a szünet utáni folytatás nem számít). */
  onStart?: () => void;
  /** Ha a felvétel végére ért. */
  onComplete?: () => void;
}) {
  const ref = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [total, setTotal] = useState(seconds ?? 0);
  const started = useRef(false);

  useEffect(() => {
    setPlaying(false);
    setCurrent(0);
    setTotal(seconds ?? 0);
    started.current = false;
  }, [src, seconds]);

  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) void el.play();
    else el.pause();
  };

  const seek = (value: number) => {
    const el = ref.current;
    if (el && Number.isFinite(el.duration)) {
      el.currentTime = value;
      setCurrent(value);
    }
  };

  const max = total || 0;
  return (
    <div className="flex flex-col gap-3 rounded-xl bg-gold-soft p-3.5">
      <div className="flex items-center gap-3.5">
        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? "Szünet" : "Lejátszás"}
          className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full bg-ink text-gold"
        >
          {playing ? (
            <Pause size={22} fill="currentColor" strokeWidth={0} />
          ) : (
            <Play size={22} fill="currentColor" strokeWidth={0} className="ml-0.5" />
          )}
        </button>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-[15px] font-semibold text-ink">{title}</span>
          {max > 0 && (
            <span className="text-[12px] tabular-nums text-gold-text">{formatDuration(max)}</span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2.5">
        <span className="w-9 shrink-0 text-[11px] tabular-nums text-gold-text">
          {formatDuration(current)}
        </span>
        <input
          type="range"
          aria-label="Pozíció"
          min={0}
          max={max || 1}
          step={1}
          value={current}
          disabled={!max}
          onChange={(e) => seek(Number(e.target.value))}
          className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-ink/10 accent-gold"
        />
        <span className="w-9 shrink-0 text-right text-[11px] tabular-nums text-gold-text">
          {formatDuration(max)}
        </span>
      </div>

      <audio
        ref={ref}
        src={src}
        preload="metadata"
        onPlay={() => {
          setPlaying(true);
          if (!started.current) {
            started.current = true;
            onStart?.();
          }
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          started.current = false;
          onComplete?.();
        }}
        onTimeUpdate={(e) => setCurrent(Math.floor(e.currentTarget.currentTime))}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d) && d > 0) setTotal(Math.floor(d));
        }}
      />
    </div>
  );
}
